import {
    ComponentRef,
    Directive,
    ElementRef,
    PLATFORM_ID,
    Renderer2,
    ViewContainerRef,
    input,
    output,
    inject,
    effect
} from '@angular/core';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';

import { createSymbolSvg, isSvgSupported, isUrlSymbol, makeIdsUnique, removeAttributes, setAttributes } from './inline-svg.util';
import { ApInlineSvgComponent } from './inline-svg.component';
import { ApInlineSvgCacheService } from './inline-svg-cache.service';
import { ApInlineSvgService } from './inline-svg.service';

@Directive({
    selector: '[apInlineSVG]',
    standalone: true
})
export class ApInlineSvgDirective {
    private readonly el = inject(ElementRef);
    private readonly viewContainerRef = inject(ViewContainerRef);
    private readonly svgCache = inject(ApInlineSvgCacheService);
    private readonly renderer = inject(Renderer2);
    private readonly inlineSvgService = inject(ApInlineSvgService);
    private readonly platformId = inject(PLATFORM_ID);

    /**
     * Required - URL where to load the SVG
     */
    public readonly apInlineSVG = input.required<string>();

    /**
     * Should the url be resolved?
     * Defaults to true
     */
    public readonly resolveSVGUrl = input(true);

    /**
     * Should the svg replace the current contents of the parent node?
     * Defaults to false
     */
    public readonly replaceContents = input(true);

    /**
     * Should the svg be inserted by directive or keep control and do it on callback ?
     * Defaults to true
     */
    public readonly insertEnabled = input(true);

    /**
     * Should it prepend before the element be replaced?
     * Defaults to false
     */
    public readonly prepend = input(false);

    /**
     * Should the SVG be injected in the Element on which the directive is placed?
     * Defaults to false
     */
    public readonly injectComponent = input(false);

    /**
     * Should the SVG be cached?
     * Defaults to true
     */
    public readonly cacheSVG = input(true);

    /**
     * SVG attrbiutes to set on the SVG
     */
    public readonly setSVGAttributes = input<{
        [key: string]: any;
    }>();

    /**
     * SVG attributes to remove
     */
    public readonly removeSVGAttributes = input<Array<string>>();

    /**
     * SVG loader function. Should return an SVG element.
     */
    public readonly svgLoaded = input<(svg: SVGElement, parent: Element | null) => SVGElement>();

    /**
     * Emits event after SVG is inserted
     */
    public readonly svgInserted = output<SVGElement>();

    /**
     * Emits event if SVG load fails
     */
    public readonly svgFailed = output<any>();

    /** @internal */
    _prevSVG: SVGElement;

    private readonly supportsSVG: boolean;
    private prevURL: string;
    private svgComp: ComponentRef<ApInlineSvgComponent>;

    constructor() {
        this.supportsSVG = isSvgSupported();

        effect(() => this.insertSVG());

        // Check if the browser supports embed SVGs
        if (!isPlatformServer(this.platformId) && !this.supportsSVG) {
            this.fail('Embed SVG are not supported by this browser');
        }
    }

    private async insertSVG(force = false): Promise<void> {
        if (this.isValidPlatform() && !this.isSSRDisabled()) {
            if (!isPlatformServer(this.platformId) && !this.supportsSVG) {
                return;
            }

            // Check if a URL was actually passed into the directive
            const apInlineSVG = this.apInlineSVG();
            if (!apInlineSVG) {
                this.fail('No URL passed to [inlineSVG]');
                return;
            }

            // Short circuit if SVG URL hasn't changed
            if (!force && apInlineSVG === this.prevURL) {
                return;
            }
            this.prevURL = apInlineSVG;

            try {
                const svg = await this.svgCache.getSVG(apInlineSVG, this.resolveSVGUrl());
                let symbolSvg = svg;
                if (isUrlSymbol(apInlineSVG)) {
                    const symbolId = apInlineSVG.split('#')[1];
                    symbolSvg = createSymbolSvg(this.renderer, symbolSvg, symbolId);
                }

                this.processSvg(symbolSvg);
            } catch (e) {
                this.fail(e);
            }
        }
    }

    /**
     * The actual processing (manipulation, lifecycle, etc.) and displaying of the
     * SVG.
     *
     * @param svg The SVG to display within the directive element.
     */
    private processSvg(svg: SVGElement) {
        if (!svg) {
            return;
        }

        let svgElement = svg;

        const removeSVGAttributes = this.removeSVGAttributes();
        if (removeSVGAttributes && isPlatformBrowser(this.platformId)) {
            removeAttributes(svgElement, removeSVGAttributes);
        }

        const setSVGAttributes = this.setSVGAttributes();
        if (setSVGAttributes) {
            setAttributes(svgElement, setSVGAttributes);
        }

        const svgLoaded = this.svgLoaded();
        if (svgLoaded) {
            svgElement = svgLoaded(svgElement, this.el.nativeElement);
        }

        svgElement = makeIdsUnique(svgElement);

        if (this.insertEnabled()) {
            this.insertElement(svgElement);
        }

        this.svgInserted.emit(svgElement);
    }

    /**
     * Handles the insertion of the directive contents, which could be an SVG
     * element or a component.
     *
     * @param el The element to put within the directive.
     */
    private insertElement(el: HTMLElement | SVGElement): void {
        if (this.injectComponent()) {
            if (!this.svgComp) {
                this.svgComp = this.viewContainerRef.createComponent(ApInlineSvgComponent);
            }

            this.svgComp.setInput('context', this);
            this.svgComp.setInput('replaceContents', this.replaceContents());
            this.svgComp.setInput('prepend', this.prepend());
            this.svgComp.setInput('content', el);

            // Force element to be inside the directive element inside of adjacent
            this.renderer.appendChild(this.el.nativeElement, this.svgComp.injector.get(ApInlineSvgComponent).element.nativeElement);
        } else {
            this.inlineSvgService.insertElement(this, this.el.nativeElement, el, this.replaceContents(), this.prepend());
        }
    }

    private fail(msg: string): void {
        this.svgFailed.emit(msg);
    }

    private isValidPlatform(): boolean {
        return isPlatformServer(this.platformId) || isPlatformBrowser(this.platformId);
    }

    private isSSRDisabled(): boolean {
        return isPlatformServer(this.platformId);
    }
}
